import { Link } from "react-router-dom";
import { fmtCurrency, fmtDateShort } from "@/lib/api";
import {
  Activity,
  ShoppingBag,
  CreditCard,
  Truck,
  PackageCheck,
  Paintbrush,
} from "lucide-react";

// ---------- helpers ----------
function relTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const s = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const META = {
  order_new: { icon: ShoppingBag, tint: "bg-primary/10 text-primary", label: "New order" },
  payment_new: { icon: CreditCard, tint: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400", label: "Payment received" },
  delivery_scheduled: { icon: Truck, tint: "bg-[#3d7a8c]/10 text-[#1f5060]", label: "Delivery scheduled" },
  delivery_delivered: { icon: PackageCheck, tint: "bg-primary/10 text-primary", label: "Delivered" },
  paint_returned: { icon: Paintbrush, tint: "bg-[#D66853]/10 text-[#D66853]", label: "Back from paint" },
};

const FALLBACK_META = { icon: Activity, tint: "bg-secondary text-foreground", label: null };

// ---------- per-type subtitle ----------
function ActivitySubtitle({ event: e }) {
  switch (e.type) {
    case "order_new":
      return (
        <>
          <span className="font-mono">{e.order_number}</span> · {e.customer} · {fmtCurrency(e.amount || 0)}
        </>
      );
    case "payment_new":
      return (
        <>
          {fmtCurrency(e.amount || 0)}
          {e.order_number && <> · <span className="font-mono">{e.order_number}</span></>}
          {e.customer && <> · {e.customer}</>}
          {(e.card_brand || e.ach_bank_name) && (
            <> · <span className="capitalize">{e.card_brand || e.ach_bank_name}</span></>
          )}
        </>
      );
    case "delivery_scheduled":
      return (
        <>
          <span className="font-mono">{e.order_number}</span> · {e.customer}
          {e.scheduled_for && <> · {fmtDateShort(e.scheduled_for)}</>}
        </>
      );
    case "delivery_delivered":
      return (
        <>
          <span className="font-mono">{e.order_number}</span> · {e.customer}
        </>
      );
    case "paint_returned":
      return (
        <>
          Batch <span className="font-mono">{e.batch_number}</span>
          {e.vendor && <> · {e.vendor}</>}
          {e.units ? <> · {e.units} units</> : null}
        </>
      );
    default:
      return null;
  }
}

function activityLink(e) {
  if (e.type === "paint_returned") return "/paint";
  if (e.order_id) return `/orders/${e.order_id}`;
  return null;
}

function activityKey(e, index) {
  // Prefer the *most-specific* id per event type so multiple deliveries for
  // the same order (or multiple paint batches) don't collide.
  const specific =
    e.delivery_id ||
    e.batch_id ||
    e.order_id ||
    "";
  return `${e.type}-${specific}-${e.at || index}`;
}

// ---------- row ----------
function ActivityRow({ event: e, index }) {
  const meta = META[e.type] || { ...FALLBACK_META, label: e.type };
  const Icon = meta.icon;
  const to = activityLink(e);
  const row = (
    <div className="flex gap-3">
      <div className={`w-8 h-8 rounded-md grid place-items-center shrink-0 ${meta.tint}`}>
        <Icon className="w-4 h-4" strokeWidth={1.75} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm flex items-baseline justify-between gap-3">
          <span className="font-medium truncate">{meta.label}</span>
          <span className="text-xs text-muted-foreground shrink-0 num">{relTime(e.at)}</span>
        </div>
        <div className="text-xs text-muted-foreground truncate">
          <ActivitySubtitle event={e} />
        </div>
      </div>
    </div>
  );
  return (
    <li data-testid={`activity-${e.type}-${index}`}>
      {to ? (
        <Link
          to={to}
          className="block rounded-md -mx-2 px-2 py-1 hover:bg-secondary/60 transition-colors"
        >
          {row}
        </Link>
      ) : (
        row
      )}
    </li>
  );
}

// ---------- main ----------
export default function RecentActivity({ events }) {
  const rows = events.slice(0, 12);
  return (
    <div className="surface-card p-6" data-testid="recent-activity">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-primary" />
          <div className="label-eyebrow">Recent activity</div>
        </div>
        <span className="text-xs text-muted-foreground">Live · updates every 60s</span>
      </div>
      {rows.length === 0 && (
        <div className="text-sm text-muted-foreground py-2">
          No recent activity yet. Sync Base44 or wait for the next Stripe charge.
        </div>
      )}
      <ul className="space-y-4">
        {rows.map((e, i) => (
          <ActivityRow key={activityKey(e, i)} event={e} index={i} />
        ))}
      </ul>
    </div>
  );
}
